import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Menu,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Paper,
} from "@mui/material";
import {
  Category,
  Menu as MenuIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { BiHelpCircle } from "react-icons/bi";
import { Link } from "react-router-dom";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { CategoryOption } from "@/pages/seller/articles";
import useOffer from "@/hooks/useTender";

const topSales = [
  { id: 1, name: "Digital Thermometer", price: 29.99 },
  { id: 2, name: "Blood Pressure Monitor", price: 89.99 },
  { id: 3, name: "Pulse Oximeter", price: 39.99 },
  { id: 4, name: "First Aid Kit", price: 49.99 },
];

function StoreNav() {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const { getcategories } = useOffer();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const Categories = await getcategories();
        if (Categories) {
          const uniqueCategories = Categories.data.filter(
            (item: { name: any }, index: any, self: any[]) =>
              index === self.findIndex((t) => t.name === item.name)
          );
          const Cates: CategoryOption[] = uniqueCategories.map(
            (item: { id: any; name: any }) => ({
              value: item.id,
              label: item.name,
            })
          );
          setCategories(Cates);
        } else {
          setCategories([]);
        }
      } catch (error) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Desktop menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Mobile drawer handlers
  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => setDrawerOpen(false);

  // Category List
  const CategoryList = (
    <Box sx={{ width: { xs: 260, sm: 320 }, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Category sx={{ mr: 1 }} />
        <Typography variant="h6" fontWeight={700}>
          {t("categories.title")}
        </Typography>
        <Box flex={1} />
        <IconButton
          onClick={handleDrawerClose}
          sx={{ display: { md: "none" } }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <List>
        {categories.map((category) => (
          <ListItem key={category.value} sx={{ borderRadius: 2 }}>
            <ListItemText primary={category.label} />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t("products.top_sales")}
      </Typography>
      <Grid container spacing={1}>
        {topSales.map((product) => (
          <Grid item xs={12} sm={6} key={product.id}>
            <Paper
              component={Link}
              to={`/product/${product.id}`}
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                textDecoration: "none",
                color: "inherit",
                borderRadius: 2,
                boxShadow: 0,
                transition: "background 0.2s",
                "&:hover": { bgcolor: theme.palette.action.hover },
              }}
              elevation={0}
            >
              <Typography fontSize={14}>{product.name}</Typography>
              <Typography
                fontSize={14}
                color={theme.palette.success.main}
                fontWeight={700}
              >
                ${product.price}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
        pt: { xs: 0, md: 14 },
        pb: { xs: 0, md: 0 },
        px: { xs: 1, sm: 2, md: 6 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 56,
        }}
      >
        {/* Category Button */}
        {isMobile ? (
          <IconButton
            onClick={handleDrawerOpen}
            size="large"
            className="!text-black"
          >
            <MenuIcon />
            <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600 }}>
              {t("categories.title")}
            </Typography>
          </IconButton>
        ) : (
          <Button
            startIcon={<Category />}
            onClick={handleMenuOpen}
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            {t("categories.title")}
          </Button>
        )}

        {/* Help Link */}
        <Button
          component={Link}
          to="/help"
          startIcon={<BiHelpCircle size={22} />}
          sx={{
            color: theme.palette.text.secondary,
            fontWeight: 500,
            textTransform: "none",
            borderRadius: 2,
            px: 2,
            py: 1,
            "&:hover": { color: theme.palette.primary.main },
          }}
        >
          {t("navigation.Help")}
        </Button>
      </Box>

      {/* Desktop Category Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && !isMobile}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: locale === "ar" ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: locale === "ar" ? "right" : "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 320,
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        {CategoryList}
      </Menu>

      {/* Mobile Drawer */}
      <Drawer
        anchor={locale === "ar" ? "right" : "left"}
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {CategoryList}
      </Drawer>
    </Box>
  );
}

export default StoreNav;
